// BrandAssetsPanel — the logo library Harvi's Word/PDF documents draw on.
//
// Admin screen over /api/assistant/brand-assets. Each logo has a KEY, which is
// what a playbook names ("put `harvus-primary` left and the fund's logo
// right"), and a sentence saying what it is for, which Harvi reads too. The
// key is fixed once created — renaming it would silently break every playbook
// that names it — so an edit changes the words or replaces the file.
import { useEffect, useRef, useState } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { Btn, Spinner, ErrorMsg, Card, Input, Textarea, Modal, ModalFooter, Icon, InfoNote } from '../ui';

const MAX_BYTES = 2 * 1024 * 1024;

function readBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(new Error('Could not read that file.'));
    r.readAsDataURL(file);
  });
}

const slug = s => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);

function Thumb({ assetId, version }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let live = true;
    api.get(`assistant/brand-assets/${assetId}/image`).then(r => live && setUrl(r.url)).catch(() => {});
    return () => { live = false; };
  }, [assetId, version]);
  return (
    <div style={{ width: 120, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: B.surfaceMuted, border: `1px solid ${B.divider}`, borderRadius: 8, flexShrink: 0 }}>
      {url ? <img src={url} alt="" style={{ maxWidth: 110, maxHeight: 40, objectFit: 'contain' }} />
        : <Icon name="image" size={16} />}
    </div>
  );
}

function LogoModal({ existing, onClose, onSaved }) {
  const [key, setKey] = useState(existing?.key || '');
  const [keyTouched, setKeyTouched] = useState(!!existing);
  const [label, setLabel] = useState(existing?.label || '');
  const [description, setDescription] = useState(existing?.description || '');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const pick = (f) => {
    setError(null);
    if (!f) return;
    if (f.size > MAX_BYTES) { setError('That file is over 2 MB — export the logo at screen size.'); return; }
    if (!/^image\/(png|jpeg)$/.test(f.type)) {
      setError(/svg/.test(f.type)
        ? 'SVG is not supported — export the logo as a PNG (transparent background, at least 600 px wide).'
        : 'Choose a PNG or JPEG file.');
      return;
    }
    setFile(f);
  };

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const body = { label, description };
      if (file) { body.fileName = file.name; body.contentBase64 = await readBase64(file); }
      if (existing) await api.put(`assistant/brand-assets/${existing.assetId}`, body, { timeout: 60000 });
      else await api.post('assistant/brand-assets', { ...body, key }, { timeout: 60000 });
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const canSave = !saving && label.trim() && (existing || (key && file));
  return (
    <Modal title={existing ? `Edit ${existing.key}` : 'Add a logo'} onClose={onClose}>
      <Input label="Name" value={label} required placeholder="Harvus — primary"
        onChange={(v) => { setLabel(v); if (!keyTouched) setKey(slug(v)); }} />
      {existing ? (
        <div style={{ fontSize: 13, color: B.textMd, marginBottom: 16 }}>
          Key: <code>{existing.key}</code> — fixed, because playbooks name it.
        </div>
      ) : (
        <Input label="Key (what a playbook calls it)" value={key} required placeholder="harvus-primary"
          onChange={(v) => { setKeyTouched(true); setKey(slug(v)); }} />
      )}
      <Textarea label="What it's for" value={description} rows={2}
        placeholder="Firm logo — top left of every IC memo and investor letter."
        onChange={setDescription} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg" style={{ display: 'none' }}
          onChange={e => pick(e.target.files?.[0])} />
        <Btn variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
          <Icon name="upload" size={14} /> {existing ? 'Replace file' : 'Choose file'}
        </Btn>
        <span style={{ fontSize: 13, color: B.textMd }}>
          {file ? file.name : existing ? 'Keeping the current file' : 'PNG or JPEG, up to 2 MB'}
        </span>
      </div>
      {error && <ErrorMsg msg={error} />}
      <ModalFooter>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={!canSave} loading={saving}>Save</Btn>
      </ModalFooter>
    </Modal>
  );
}

export const BrandAssetsPanel = () => {
  const [reload, setReload] = useState(0);
  const [editing, setEditing] = useState(null); // null | 'new' | asset
  const [deleting, setDeleting] = useState(null);
  const [actionErr, setActionErr] = useState(null);
  const { data, loading, error } = useApi(() => api.get('assistant/brand-assets'), [reload]);
  const items = data?.items || [];

  const remove = async (a) => {
    if (!window.confirm(`Delete the logo "${a.label}"? Playbooks that name "${a.key}" will draft without it.`)) return;
    setDeleting(a.assetId); setActionErr(null);
    try { await api.del(`assistant/brand-assets/${a.assetId}`); setReload(n => n + 1); }
    catch (e) { setActionErr(e.message); }
    finally { setDeleting(null); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '28px 0 4px', gap: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: B.text }}>Brand assets (logos)</div>
        {data?.available !== false && (
          <Btn size="sm" onClick={() => setEditing('new')} disabled={data?.storage === false}>
            <Icon name="imagePlus" size={14} /> Add logo
          </Btn>
        )}
      </div>
      <div style={{ fontSize: 13, color: B.textMd, marginBottom: 14 }}>
        Logos Harvi can place across the top of a Word or PDF document. A playbook says which logo goes where by its
        key; Harvi uses a logo only when a playbook names one or you ask for it.
      </div>
      {data?.available === false && <InfoNote>The logo library needs migration 218, which runs on the next deploy.</InfoNote>}
      {data?.storage === false && <InfoNote>No storage account is configured, so logos cannot be uploaded here.</InfoNote>}
      {actionErr && <ErrorMsg msg={actionErr} />}
      <Card style={{ padding: 0 }}>
        {loading ? <div style={{ padding: 20 }}><Spinner /></div>
          : error ? <div style={{ padding: 20 }}><ErrorMsg msg={error} /></div>
          : !items.length ? (
            <div style={{ padding: 20, fontSize: 13, color: B.textMd }}>No logos yet.</div>
          ) : items.map(a => (
            <div key={a.assetId} style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '12px 16px',
                                          borderBottom: `1px solid ${B.border}` }}>
              <Thumb assetId={a.assetId} version={a.modifiedUTC} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: B.text }}>
                  {a.label} <code style={{ fontSize: 12, color: B.textMd, fontWeight: 400 }}>{a.key}</code>
                </div>
                <div style={{ fontSize: 13, color: a.description ? B.textMd : B.textLt }}>
                  {a.description || 'No description — add one so Harvi and playbook authors know what it is for.'}
                </div>
                <div style={{ fontSize: 12, color: B.textLt }}>
                  {a.pixelWidth}×{a.pixelHeight} px · {Math.max(1, Math.round((a.byteSize || 0) / 1024))} KB
                </div>
              </div>
              <Btn size="sm" variant="secondary" onClick={() => setEditing(a)}>Edit</Btn>
              <Btn size="sm" variant="secondary" onClick={() => remove(a)} disabled={deleting === a.assetId}
                aria-label={`Delete ${a.label}`}>
                <Icon name="trash" size={14} />
              </Btn>
            </div>
          ))}
      </Card>
      {editing && (
        <LogoModal existing={editing === 'new' ? null : editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setReload(n => n + 1); }} />
      )}
    </div>
  );
};

export default BrandAssetsPanel;
